import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import type {
  TFinancialConditionCertificateGenerationResponse,
  TIncomeStructureByKvedCertificateGenerationResponse,
  TStateAidCertificateGenerationResponse,
  TThunkError,
} from '~/store/covenants/types.ts';
import type {
  TFinancialConditionCertificateDto,
  TFinancialConditionCertificateResponse,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import type {
  TIncomeStructureByKvedCertificateDto,
  TIncomeStructureByKvedCertificateResponse,
  TIncomeStructureByKvedCertificateSaveResponse,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/types.ts';
import type {
  StateAidCertificateType,
  TStateAidCertificateDto,
  TStateAidCertificateResponse,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/stateAidReceived/types.ts';
import { STATE_AID_CERTIFICATE_CONFIG } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/stateAidReceived/constants.ts';
import { thunkErrorHandler } from '~/store/utils.ts';
import api from '~/libs/api.ts';

export const saveIncomeStructureByKvedCertificate = createAsyncThunk<
  TIncomeStructureByKvedCertificateSaveResponse,
  {
    contragentId: number;
    applicationId: string;
    yearPeriod: string;
    childPeriod: string;
    version: number;
    data: TIncomeStructureByKvedCertificateDto;
  },
  { rejectValue: TThunkError }
>(
  'covenantsPage/saveIncomeStructureByKvedCertificate',
  async (
    { contragentId, applicationId, yearPeriod, childPeriod, version, data },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post<TIncomeStructureByKvedCertificateSaveResponse>(
        `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/certificates/income-structure-by-kved`,
        data,
        { params: { yearPeriod, childPeriod, version } }
      );

      return response.data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const generateIncomeStructureByKvedCertificate = createAsyncThunk<
  TIncomeStructureByKvedCertificateGenerationResponse,
  {
    contragentId: number;
    applicationId: string;
    yearPeriod: string;
    childPeriod: string;
  },
  { rejectValue: TThunkError }
>(
  'covenantsPage/generateIncomeStructureByKvedCertificate',
  async ({ contragentId, applicationId, yearPeriod, childPeriod }, { rejectWithValue }) => {
    try {
      const response = await api.post<TIncomeStructureByKvedCertificateGenerationResponse>(
        `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/certificates/income-structure-by-kved/generate`,
        undefined,
        { params: { yearPeriod, childPeriod } }
      );

      return response.data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchIncomeStructureByKvedCertificate = createAsyncThunk<
  TIncomeStructureByKvedCertificateResponse | null,
  {
    contragentId: number;
    applicationId: string;
    yearPeriod: string;
    childPeriod: string;
  },
  { rejectValue: TThunkError }
>(
  'covenantsPage/fetchIncomeStructureByKvedCertificate',
  async ({ contragentId, applicationId, yearPeriod, childPeriod }, { rejectWithValue }) => {
    try {
      const response = await api.get<{
        type: 'INCOME_STRUCTURE_BY_KVED';
        body?: TIncomeStructureByKvedCertificateDto | null;
        fullName?: string;
        borrowerEdrpou?: string;
        childPeriod?: string;
        version: number;
        fop: boolean;
      }>(
        `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/certificates`,
        {
          params: {
            certificateType: 'INCOME_STRUCTURE_BY_KVED',
            yearPeriod,
            childPeriod,
          },
        }
      );
      const body = response.data.body;

      return {
        type: response.data.type,
        body: body && Object.keys(body).length ? body : null,
        version: response.data.version,
        fullName: response.data.fullName,
        borrowerEdrpou: response.data.borrowerEdrpou,
        childPeriod: response.data.childPeriod,
        yearPeriod,
        fop: response.data.fop,
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }

      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const saveFinancialConditionCertificate = createAsyncThunk<
  string,
  {
    contragentId: number;
    applicationId: string;
    version: number;
    data: TFinancialConditionCertificateDto;
  },
  { rejectValue: TThunkError }
>(
  'covenantsPage/saveFinancialConditionCertificate',
  async ({ contragentId, applicationId, version, data }, { rejectWithValue }) => {
    try {
      const response = await api.post<string>(
        `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/certificates/financial-condition`,
        data,
        { params: { version } }
      );

      return response.data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchFinancialConditionCertificate = createAsyncThunk<
  TFinancialConditionCertificateResponse | null,
  {
    contragentId: number;
    applicationId: string;
    yearPeriod: string;
    childPeriod: string;
  },
  { rejectValue: TThunkError }
>(
  'covenantsPage/fetchFinancialConditionCertificate',
  async ({ contragentId, applicationId, yearPeriod, childPeriod }, { rejectWithValue }) => {
    try {
      const response = await api.get<{
        type: 'FINANCIAL_CONDITION';
        body?: Partial<TFinancialConditionCertificateDto> | null;
        fullName?: string;
        borrowerEdrpou?: string;
        asOfDay?: string;
        childPeriod?: string;
        version: number;
        fop: boolean;
      }>(
        `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/certificates`,
        {
          params: {
            certificateType: 'FINANCIAL_CONDITION',
            yearPeriod,
            childPeriod,
          },
        }
      );
      const data = response.data.body;

      return {
        type: response.data.type,
        body: data && Object.keys(data).length ? (data as TFinancialConditionCertificateDto) : null,
        version: response.data.version,
        fullName: response.data.fullName,
        borrowerEdrpou: response.data.borrowerEdrpou,
        asOfDay: response.data.asOfDay,
        childPeriod: response.data.childPeriod,
        yearPeriod,
        fop: response.data.fop,
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }

      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const generateFinancialConditionCertificate = createAsyncThunk<
  TFinancialConditionCertificateGenerationResponse,
  {
    contragentId: number;
    applicationId: string;
    isFop: boolean;
    yearPeriod: string;
    childPeriod: string;
  },
  { rejectValue: TThunkError }
>(
  'covenantsPage/generateFinancialConditionCertificate',
  async ({ contragentId, applicationId, isFop, yearPeriod, childPeriod }, { rejectWithValue }) => {
    try {
      const response = await api.post<TFinancialConditionCertificateGenerationResponse>(
        `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/certificates/financial-condition/generate`,
        undefined,
        { params: { isFop, yearPeriod, childPeriod } }
      );

      return response.data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchStateAidCertificate = createAsyncThunk<
  TStateAidCertificateResponse | null,
  {
    contragentId: number;
    applicationId: string;
    yearPeriod: string;
    childPeriod: string;
    stateAidCertificateType: StateAidCertificateType;
  },
  { rejectValue: TThunkError }
>(
  'covenantsPage/fetchStateAidCertificate',
  async (
    { contragentId, applicationId, yearPeriod, childPeriod, stateAidCertificateType },
    { rejectWithValue }
  ) => {
    try {
      const config = STATE_AID_CERTIFICATE_CONFIG[stateAidCertificateType];
      const response = await api.get<{
        type: TStateAidCertificateResponse['type'];
        body?: TStateAidCertificateDto | null;
        version: number;
        fullName?: string;
        borrowerEdrpou?: string;
        fop: boolean;
      }>(
        `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/certificates`,
        { params: { certificateType: config.certificateType, yearPeriod, childPeriod } }
      );
      const body = response.data.body;

      return {
        ...response.data,
        body: body && Object.keys(body).length ? body : null,
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) return null;

      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const saveStateAidCertificate = createAsyncThunk<
  void,
  {
    contragentId: number;
    applicationId: string;
    version: number;
    stateAidCertificateType: StateAidCertificateType;
    data: TStateAidCertificateDto;
  },
  { rejectValue: TThunkError }
>(
  'covenantsPage/saveStateAidCertificate',
  async (
    { contragentId, applicationId, version, stateAidCertificateType, data },
    { rejectWithValue }
  ) => {
    try {
      const config = STATE_AID_CERTIFICATE_CONFIG[stateAidCertificateType];
      await api.post(
        `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/certificates/${config.endpoint}`,
        data,
        { params: { version } }
      );
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const generateStateAidCertificate = createAsyncThunk<
  TStateAidCertificateGenerationResponse,
  {
    contragentId: number;
    applicationId: string;
    yearPeriod: string;
    childPeriod: string;
    stateAidCertificateType: StateAidCertificateType;
  },
  { rejectValue: TThunkError }
>(
  'covenantsPage/generateStateAidCertificate',
  async (
    { contragentId, applicationId, yearPeriod, childPeriod, stateAidCertificateType },
    { rejectWithValue }
  ) => {
    try {
      const config = STATE_AID_CERTIFICATE_CONFIG[stateAidCertificateType];
      const response = await api.post<TStateAidCertificateGenerationResponse>(
        `/covenants-api/covenants/contragents/${contragentId}/applications/${applicationId}/certificates/${config.endpoint}/generate`,
        undefined,
        { params: { yearPeriod, childPeriod } }
      );

      return response.data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);
